import React, { useEffect, useState } from 'react';

import socialInboxClient from 'api/socialInboxClient';
import { downloadWhatsAppMedia } from 'features/chat/chatActions';
import {
  getWhatsAppFilename,
  getWhatsAppMediaId,
  isWhatsAppMediaId,
} from 'utils/whatsappMediaUtils';

import {
  AttachmentAudio,
  AttachmentContainer,
  AttachmentFallbackIcon,
  AttachmentFallbackLink,
  AttachmentFileIcon,
  AttachmentFileInfo,
  AttachmentFileLink,
  AttachmentFileName,
  AttachmentFileSize,
  AttachmentImage,
  AttachmentImageLink,
  AttachmentVideo,
  ClickableAttachmentLink,
  ClickableFileLink,
  ContactCard,
  ContactIcon,
  ContactInfo,
  ContactName,
  ContactPhone,
  LoadingSpinner,
  LoadingSpinnerContainer,
} from '../ChatSingle.styles';

const AttachmentRenderer = ({
  attachments,
  isMyMessage = false,
  platform,
  message,
  dispatch,
}) => {
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [videoBlobUrls, setVideoBlobUrls] = useState({});
  const [audioBlobUrls, setAudioBlobUrls] = useState({});
  const [loadingMedia, setLoadingMedia] = useState({});
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  if (!attachments || attachments.length === 0) return null;

  const handleWhatsAppFileDownload = async (e, attachment) => {
    e.preventDefault();

    const mediaId = getWhatsAppMediaId(message);
    if (!mediaId) {
      console.error('No media ID found for WhatsApp attachment');
      return;
    }

    const filename = getWhatsAppFilename(attachment, message);

    try {
      await dispatch(downloadWhatsAppMedia({ mediaId, filename })).unwrap();
    } catch (error) {
      console.error('Failed to download WhatsApp media:', error);
    }
  };

  const loadWhatsAppMedia = async (mediaId, index, type) => {
    if (
      loadingMedia[index] ||
      imageBlobUrls[index] ||
      videoBlobUrls[index] ||
      audioBlobUrls[index]
    ) {
      return;
    }

    setLoadingMedia((prev) => ({ ...prev, [index]: true }));

    try {
      const response = await socialInboxClient.get(
        `/whatsapp/media/${mediaId}`,
        { responseType: 'blob' },
        null
      );

      const blob = new Blob([response], { type: response.type || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);

      if (type === 'image') {
        setImageBlobUrls((prev) => ({ ...prev, [index]: blobUrl }));
      } else if (type === 'video') {
        setVideoBlobUrls((prev) => ({ ...prev, [index]: blobUrl }));
      } else if (type === 'audio') {
        setAudioBlobUrls((prev) => ({ ...prev, [index]: blobUrl }));
      }
    } catch (error) {
      console.error('Failed to load WhatsApp media:', error);
    } finally {
      setLoadingMedia((prev) => ({ ...prev, [index]: false }));
    }
  };

  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach((url) => window.URL.revokeObjectURL(url));
      Object.values(videoBlobUrls).forEach((url) => window.URL.revokeObjectURL(url));
      Object.values(audioBlobUrls).forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [imageBlobUrls, videoBlobUrls, audioBlobUrls]);

  return (
    <AttachmentContainer>
      {attachments.map((attachment, index) => {
        if (!attachment || !attachment.type) {
          return null;
        }

        const { type } = attachment;

        const payload = attachment.payload || attachment;

        if (!payload.url) {
          return null;
        }

        const isWhatsAppMedia = platform === 'whatsapp' && isWhatsAppMediaId(payload.url);

        if (type === 'image') {
          const isSticker = payload.sticker_id;

          if (isWhatsAppMedia) {
            const mediaId = getWhatsAppMediaId(message);
            const blobUrl = imageBlobUrls[index];
            const isLoading = loadingMedia[index];

            if (!blobUrl && !isLoading && mediaId) {
              loadWhatsAppMedia(mediaId, index, 'image');
            }

            return (
              <ClickableAttachmentLink
                key={index}
                href="#"
                onClick={(e) => handleWhatsAppFileDownload(e, attachment)}
              >
                {blobUrl ? (
                  <AttachmentImage
                    src={blobUrl}
                    alt={isSticker ? 'Sticker' : 'Image'}
                    $isSticker={isSticker}
                  />
                ) : isLoading ? (
                  <LoadingSpinnerContainer>
                    <LoadingSpinner />
                  </LoadingSpinnerContainer>
                ) : (
                  <AttachmentImage
                    src={message?.rawPayload?.image?.url || payload.url}
                    alt={isSticker ? 'Sticker' : 'Image'}
                    $isSticker={isSticker}
                    onError={() => {
                      if (mediaId && !blobUrl && !isLoading) {
                        loadWhatsAppMedia(mediaId, index, 'image');
                      }
                    }}
                  />
                )}
              </ClickableAttachmentLink>
            );
          }

          if (imageLoadErrors[index]) {
            return null;
          }

          return (
            <AttachmentImageLink
              key={index}
              href={payload.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AttachmentImage
                src={payload.url}
                alt={isSticker ? 'Sticker' : 'Image'}
                $isSticker={isSticker}
                onError={() => {
                  setImageLoadErrors((prev) => ({ ...prev, [index]: true }));
                }}
              />
            </AttachmentImageLink>
          );
        }

        // Handle videos
        if (type === 'video') {
          if (isWhatsAppMedia) {
            const mediaId = getWhatsAppMediaId(message);
            const blobUrl = videoBlobUrls[index];
            const isLoading = loadingMedia[index];
            const videoUrl = message?.rawPayload?.video?.url;

            if (!blobUrl && !isLoading && mediaId) {
              loadWhatsAppMedia(mediaId, index, 'video');
            }

            const videoSrc = blobUrl || videoUrl || payload.url;

            return isLoading ? (
              <LoadingSpinnerContainer key={index}>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            ) : (
              <AttachmentVideo key={index} controls>
                <source src={videoSrc} type={payload.mime_type || 'video/mp4'} />
                Your browser does not support the video tag.
              </AttachmentVideo>
            );
          }

          return (
            <AttachmentVideo key={index} controls>
              <source src={payload.url} type={payload.mime_type || 'video/mp4'} />
              Your browser does not support the video tag.
            </AttachmentVideo>
          );
        }

        // Handle audio
        if (type === 'audio') {
          if (isWhatsAppMedia) {
            const mediaId = getWhatsAppMediaId(message);
            const blobUrl = audioBlobUrls[index];
            const isLoading = loadingMedia[index];
            const audioUrl = message?.rawPayload?.audio?.url;

            if (!blobUrl && !isLoading && mediaId) {
              loadWhatsAppMedia(mediaId, index, 'audio');
            }

            const audioSrc = blobUrl || audioUrl || payload.url;

            return isLoading ? (
              <LoadingSpinnerContainer key={index}>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            ) : (
              <AttachmentAudio key={index} controls>
                <source src={audioSrc} type={payload.mime_type || 'audio/mpeg'} />
                Your browser does not support the audio tag.
              </AttachmentAudio>
            );
          }

          return (
            <AttachmentAudio key={index} controls>
              <source src={payload.url} type={payload.mime_type || 'audio/mpeg'} />
              Your browser does not support the audio tag.
            </AttachmentAudio>
          );
        }

        // Handle files
        if (type === 'file') {
          const fileName = payload.name || payload.filename || 'Download File';
          const fileSize = payload.size ? `(${(payload.size / 1024).toFixed(2)} KB)` : '';

          if (isWhatsAppMedia) {
            return (
              <ClickableFileLink
                key={index}
                href="#"
                onClick={(e) => handleWhatsAppFileDownload(e, attachment)}
                $isMyMessage={isMyMessage}
              >
                <AttachmentFileIcon>📎</AttachmentFileIcon>
                <AttachmentFileInfo>
                  <AttachmentFileName>{fileName}</AttachmentFileName>
                  {fileSize && <AttachmentFileSize>{fileSize}</AttachmentFileSize>}
                </AttachmentFileInfo>
              </ClickableFileLink>
            );
          }

          return (
            <AttachmentFileLink
              key={index}
              href={payload.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              $isMyMessage={isMyMessage}
            >
              <AttachmentFileIcon>📎</AttachmentFileIcon>
              <AttachmentFileInfo>
                <AttachmentFileName>{fileName}</AttachmentFileName>
                {fileSize && <AttachmentFileSize>{fileSize}</AttachmentFileSize>}
              </AttachmentFileInfo>
            </AttachmentFileLink>
          );
        }

        if (type === 'contacts' || type === 'contact') {
          const contacts = message?.rawPayload?.contacts || [];
          const contact = contacts[0] || {};
          const contactName =
            contact.name?.formatted_name || contact.name?.first_name || 'Contact';
          const contactPhone = contact.phones?.[0]?.phone || contact.phones?.[0]?.wa_id || '';

          const getInitials = (name) => {
            const nameParts = name.split(' ').filter(Boolean);
            if (nameParts.length >= 2) {
              return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
          };

          return (
            <ContactCard key={index} $isMyMessage={isMyMessage}>
              <ContactIcon>{getInitials(contactName)}</ContactIcon>
              <ContactInfo>
                <ContactName>{contactName}</ContactName>
                {contactPhone && <ContactPhone>{contactPhone}</ContactPhone>}
              </ContactInfo>
            </ContactCard>
          );
        }

        return (
          <AttachmentFallbackLink
            key={index}
            href={payload.url}
            target="_blank"
            rel="noopener noreferrer"
            $isMyMessage={isMyMessage}
          >
            <AttachmentFallbackIcon>🔗</AttachmentFallbackIcon>
            View Attachment
          </AttachmentFallbackLink>
        );
      })}
    </AttachmentContainer>
  );
};

export default AttachmentRenderer;
